import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CloudIcon,
  LinkIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  PlusIcon,
  EyeIcon,
  CalendarIcon,
  ClockIcon,
  BellIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { CloudIcon as CloudIconSolid } from '@heroicons/react/24/solid';

const CalendarIntegration = () => {
  const [integrations, setIntegrations] = useState([]);
  const [syncSettings, setSyncSettings] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('integrations');
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    // Simulate fetching integration data
    const fetchData = () => {
      const mockIntegrations = [
        {
          id: 'outlook',
          name: 'Microsoft Outlook',
          description: 'Sync with Microsoft Outlook calendar and email',
          type: 'calendar',
          provider: 'Microsoft',
          status: 'connected',
          lastSync: new Date(2024, 11, 14, 15, 30),
          syncFrequency: '15min',
          events: 145,
          icon: 'https://img.icons8.com/color/48/000000/microsoft-outlook-2019.png',
          features: ['Two-way sync', 'Email notifications', 'Meeting invites', 'Contact sync'],
          settings: {
            syncDirection: 'bidirectional',
            syncPastEvents: true,
            syncFutureEvents: true,
            syncReminders: true,
            syncAttendees: true
          }
        },
        {
          id: 'google-calendar',
          name: 'Google Calendar',
          description: 'Sync with Google Calendar and Google Workspace',
          type: 'calendar',
          provider: 'Google',
          status: 'connected',
          lastSync: new Date(2024, 11, 14, 16, 0),
          syncFrequency: '10min',
          events: 89,
          icon: 'https://img.icons8.com/color/48/000000/google-calendar.png',
          features: ['Real-time sync', 'Google Meet integration', 'Smart suggestions', 'Multiple calendars'],
          settings: {
            syncDirection: 'bidirectional',
            syncPastEvents: false,
            syncFutureEvents: true,
            syncReminders: true,
            syncAttendees: true
          }
        },
        {
          id: 'apple-calendar',
          name: 'Apple Calendar',
          description: 'Sync with Apple Calendar and iCloud',
          type: 'calendar',
          provider: 'Apple',
          status: 'disconnected',
          lastSync: null,
          syncFrequency: '30min',
          events: 0,
          icon: 'https://img.icons8.com/color/48/000000/apple-calendar.png',
          features: ['iCloud sync', 'iOS integration', 'Siri shortcuts', 'Family sharing'],
          settings: {
            syncDirection: 'bidirectional',
            syncPastEvents: true,
            syncFutureEvents: true,
            syncReminders: false,
            syncAttendees: true
          }
        },
        {
          id: 'zoom',
          name: 'Zoom',
          description: 'Integrate Zoom meetings with calendar events',
          type: 'video-conferencing',
          provider: 'Zoom',
          status: 'connected',
          lastSync: new Date(2024, 11, 14, 14, 45),
          syncFrequency: 'real-time',
          events: 23,
          icon: 'https://img.icons8.com/color/48/000000/zoom.png',
          features: ['Auto meeting links', 'Recording integration', 'Participant management', 'Waiting rooms'],
          settings: {
            autoCreateMeetings: true,
            recordMeetings: false,
            waitingRoom: true,
            participantLimit: 100
          }
        },
        {
          id: 'teams',
          name: 'Microsoft Teams',
          description: 'Integrate Microsoft Teams meetings and collaboration',
          type: 'video-conferencing',
          provider: 'Microsoft',
          status: 'error',
          lastSync: new Date(2024, 11, 13, 10, 20),
          syncFrequency: 'real-time',
          events: 67,
          icon: 'https://img.icons8.com/color/48/000000/microsoft-teams.png',
          features: ['Team meetings', 'Channel integration', 'File sharing', 'Chat integration'],
          settings: {
            autoCreateMeetings: true,
            recordMeetings: true,
            waitingRoom: false,
            participantLimit: 250
          },
          error: 'Authentication expired. Please reconnect.'
        },
        {
          id: 'slack',
          name: 'Slack',
          description: 'Get calendar notifications and reminders in Slack',
          type: 'communication',
          provider: 'Slack',
          status: 'connected',
          lastSync: new Date(2024, 11, 14, 16, 15),
          syncFrequency: 'real-time',
          events: 0,
          icon: 'https://img.icons8.com/color/48/000000/slack-new.png',
          features: ['Event notifications', 'Meeting reminders', 'Status updates', 'Channel integration'],
          settings: {
            notifyUpcoming: true,
            notifyChanges: true,
            updateStatus: true,
            channels: ['#general', '#meetings']
          }
        }
      ];

      const mockSyncSettings = {
        globalSync: true,
        syncFrequency: '15min',
        timezone: 'America/New_York',
        conflictResolution: 'manual',
        notificationPreferences: {
          email: true,
          push: true,
          slack: true,
          sms: false
        },
        dataRetention: '1year',
        privacyMode: false
      };

      const mockNotifications = [
        {
          id: 'notif001',
          type: 'sync-success',
          title: 'Google Calendar Sync Complete',
          message: 'Successfully synced 12 events from Google Calendar',
          timestamp: new Date(2024, 11, 14, 16, 0),
          read: false,
          integration: 'google-calendar'
        },
        {
          id: 'notif002',
          type: 'sync-error',
          title: 'Microsoft Teams Authentication Error',
          message: 'Unable to sync Microsoft Teams. Please check your credentials.',
          timestamp: new Date(2024, 11, 14, 15, 45),
          read: false,
          integration: 'teams'
        },
        {
          id: 'notif003',
          type: 'sync-warning',
          title: 'Duplicate Event Detected',
          message: 'Found duplicate event "Team Meeting" across multiple calendars',
          timestamp: new Date(2024, 11, 14, 14, 30),
          read: true,
          integration: 'outlook'
        },
        {
          id: 'notif004',
          type: 'sync-info',
          title: 'Zoom Integration Updated',
          message: 'Zoom integration settings have been updated successfully',
          timestamp: new Date(2024, 11, 14, 13, 15),
          read: true,
          integration: 'zoom'
        }
      ];

      setIntegrations(mockIntegrations);
      setSyncSettings(mockSyncSettings);
      setNotifications(mockNotifications);
      setLoading(false);
    };

    setTimeout(fetchData, 500);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'bg-green-100 text-green-800';
      case 'disconnected':
        return 'bg-gray-100 text-gray-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'syncing':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'disconnected':
        return <XCircleIcon className="h-4 w-4" />;
      case 'error':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'syncing':
        return <ArrowPathIcon className="h-4 w-4 animate-spin" />;
      default:
        return <InformationCircleIcon className="h-4 w-4" />;
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'sync-success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'sync-error':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'sync-warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'sync-info':
        return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <InformationCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const formatLastSync = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const handleIntegrationToggle = (integrationId) => {
    setIntegrations(prev => prev.map(integration => {
      if (integration.id === integrationId) {
        const newStatus = integration.status === 'connected' ? 'disconnected' : 'connected';
        return {
          ...integration,
          status: newStatus,
          lastSync: newStatus === 'connected' ? new Date() : null
        };
      }
      return integration;
    }));
  };

  const handleSync = (integrationId) => {
    setIntegrations(prev => prev.map(integration => {
      if (integration.id === integrationId) {
        return {
          ...integration,
          status: 'syncing',
          lastSync: new Date()
        };
      }
      return integration;
    }));

    // Simulate sync completion
    setTimeout(() => {
      setIntegrations(prev => prev.map(integration => {
        if (integration.id === integrationId) {
          return {
            ...integration,
            status: 'connected'
          };
        }
        return integration;
      }));
    }, 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center">
                <CloudIconSolid className="h-8 w-8 text-blue-600 mr-3" />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Calendar Integration</h1>
                  <p className="mt-2 text-sm text-gray-700">
                    Connect and sync with external calendar services
                  </p>
                </div>
              </div>
              <div className="mt-4 sm:mt-0 flex items-center space-x-3">
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Integration
                </button>
                <Link
                  to="/calendar"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  Back to Calendar
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'integrations', name: 'Integrations', icon: LinkIcon },
              { id: 'sync-settings', name: 'Sync Settings', icon: Cog6ToothIcon },
              { id: 'notifications', name: 'Notifications', icon: BellIcon }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Integrations Tab */}
        {activeTab === 'integrations' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircleIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Connected</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {integrations.filter(i => i.status === 'connected').length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Errors</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {integrations.filter(i => i.status === 'error').length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <CloudIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Events</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {integrations.reduce((sum, i) => sum + i.events, 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <ArrowPathIcon className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Last Sync</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {formatLastSync(Math.max(...integrations.filter(i => i.lastSync).map(i => i.lastSync)))}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Integrations List */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Available Integrations</h3>
                <div className="space-y-4">
                  {integrations.map(integration => (
                    <div key={integration.id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start">
                          <img
                            src={integration.icon}
                            alt={integration.name}
                            className="h-12 w-12 rounded-lg"
                          />
                          <div className="ml-4 flex-1">
                            <div className="flex items-center mb-2">
                              <h4 className="text-lg font-semibold text-gray-900 mr-3">
                                {integration.name}
                              </h4>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(integration.status)}`}>
                                {getStatusIcon(integration.status)}
                                <span className="ml-1 capitalize">{integration.status}</span>
                              </span>
                            </div>

                            <p className="text-gray-600 mb-3">{integration.description}</p>

                            {integration.error && (
                              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-sm text-red-700">{integration.error}</p>
                              </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                              <div className="flex items-center">
                                <ClockIcon className="h-4 w-4 mr-2" />
                                Last sync: {formatLastSync(integration.lastSync)}
                              </div>
                              <div className="flex items-center">
                                <ArrowPathIcon className="h-4 w-4 mr-2" />
                                Frequency: {integration.syncFrequency}
                              </div>
                              <div className="flex items-center">
                                <CalendarIcon className="h-4 w-4 mr-2" />
                                Events: {integration.events}
                              </div>
                            </div>

                            <div className="mt-3">
                              <div className="text-sm font-medium text-gray-700 mb-2">Features:</div>
                              <div className="flex flex-wrap gap-1">
                                {integration.features.map(feature => (
                                  <span
                                    key={feature}
                                    className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700"
                                  >
                                    {feature}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 ml-4">
                          {integration.status === 'connected' && (
                            <button
                              onClick={() => handleSync(integration.id)}
                              className="p-2 text-gray-400 hover:text-blue-600 rounded-md hover:bg-blue-50"
                              title="Sync now"
                            >
                              <ArrowPathIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                            title="View settings"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleIntegrationToggle(integration.id)}
                            className={`px-4 py-2 text-sm font-medium rounded-md ${
                              integration.status === 'connected'
                                ? 'text-red-700 bg-red-100 hover:bg-red-200'
                                : 'text-blue-700 bg-blue-100 hover:bg-blue-200'
                            }`}
                          >
                            {integration.status === 'connected' ? 'Disconnect' : 'Connect'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sync Settings Tab */}
        {activeTab === 'sync-settings' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6">Synchronization Settings</h3>

              <div className="space-y-6">
                {/* Global Settings */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Global Settings</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Default Sync Frequency
                      </label>
                      <select
                        value={syncSettings.syncFrequency}
                        onChange={(e) => setSyncSettings(prev => ({ ...prev, syncFrequency: e.target.value }))}
                        className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="5min">Every 5 minutes</option>
                        <option value="15min">Every 15 minutes</option>
                        <option value="30min">Every 30 minutes</option>
                        <option value="1hour">Every hour</option>
                        <option value="manual">Manual only</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Timezone
                      </label>
                      <select
                        value={syncSettings.timezone}
                        onChange={(e) => setSyncSettings(prev => ({ ...prev, timezone: e.target.value }))}
                        className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="America/New_York">Eastern Time</option>
                        <option value="America/Chicago">Central Time</option>
                        <option value="America/Denver">Mountain Time</option>
                        <option value="America/Los_Angeles">Pacific Time</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Conflict Resolution
                      </label>
                      <select
                        value={syncSettings.conflictResolution}
                        onChange={(e) => setSyncSettings(prev => ({ ...prev, conflictResolution: e.target.value }))}
                        className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="manual">Manual resolution</option>
                        <option value="apollo-wins">Apollo Calendar wins</option>
                        <option value="external-wins">External calendar wins</option>
                        <option value="latest-wins">Latest change wins</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Data Retention
                      </label>
                      <select
                        value={syncSettings.dataRetention}
                        onChange={(e) => setSyncSettings(prev => ({ ...prev, dataRetention: e.target.value }))}
                        className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="30days">30 days</option>
                        <option value="3months">3 months</option>
                        <option value="6months">6 months</option>
                        <option value="1year">1 year</option>
                        <option value="forever">Forever</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Notification Preferences */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Notification Preferences</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { key: 'email', name: 'Email', icon: EnvelopeIcon },
                      { key: 'push', name: 'Push Notifications', icon: DevicePhoneMobileIcon },
                      { key: 'slack', name: 'Slack', icon: ComputerDesktopIcon },
                      { key: 'sms', name: 'SMS', icon: DevicePhoneMobileIcon }
                    ].map(option => (
                      <div key={option.key} className="flex items-center">
                        <input
                          id={`notification-${option.key}`}
                          type="checkbox"
                          checked={syncSettings.notificationPreferences?.[option.key] || false}
                          onChange={(e) => setSyncSettings(prev => ({
                            ...prev,
                            notificationPreferences: {
                              ...prev.notificationPreferences,
                              [option.key]: e.target.checked
                            }
                          }))}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`notification-${option.key}`} className="ml-3 flex items-center text-sm text-gray-700">
                          <option.icon className="h-4 w-4 mr-2" />
                          {option.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Privacy Settings */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Privacy & Security</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-700">Global Sync</div>
                        <div className="text-sm text-gray-500">Enable synchronization across all connected services</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={syncSettings.globalSync}
                        onChange={(e) => setSyncSettings(prev => ({ ...prev, globalSync: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-700">Privacy Mode</div>
                        <div className="text-sm text-gray-500">Hide sensitive event details during sync</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={syncSettings.privacyMode}
                        onChange={(e) => setSyncSettings(prev => ({ ...prev, privacyMode: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700">
                    Save Settings
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">Sync Notifications</h3>
                <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                  Mark All as Read
                </button>
              </div>

              <div className="space-y-4">
                {notifications.map(notification => (
                  <div key={notification.id} className={`p-4 rounded-lg border ${
                    notification.read ? 'bg-white border-gray-200' : 'bg-blue-50 border-blue-200'
                  }`}>
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-gray-900">
                            {notification.title}
                          </h4>
                          <span className="text-xs text-gray-500">
                            {notification.timestamp.toLocaleTimeString('en-US', { 
                              hour: '2-digit', 
                              minute: '2-digit',
                              hour12: true 
                            })}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">
                            Integration: {integrations.find(i => i.id === notification.integration)?.name}
                          </span>
                          {!notification.read && (
                            <button className="text-xs text-blue-600 hover:text-blue-800">
                              Mark as Read
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {notifications.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No notifications yet
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Integration Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Add New Integration
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-gray-600">
                Choose from available integrations to connect with your calendar:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { name: 'Apple Calendar', icon: 'https://img.icons8.com/color/48/000000/apple-calendar.png', available: true },
                  { name: 'Google Meet', icon: 'https://img.icons8.com/color/48/000000/google-meet.png', available: true },
                  { name: 'Webex', icon: 'https://img.icons8.com/color/48/000000/cisco-webex.png', available: false },
                  { name: 'Discord', icon: 'https://img.icons8.com/color/48/000000/discord-logo.png', available: false }
                ].map(service => (
                  <div key={service.name} className={`p-4 border rounded-lg ${
                    service.available ? 'border-gray-200 hover:border-blue-300 cursor-pointer' : 'border-gray-100 bg-gray-50'
                  }`}>
                    <div className="flex items-center">
                      <img src={service.icon} alt={service.name} className="h-8 w-8" />
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">{service.name}</div>
                        <div className="text-xs text-gray-500">
                          {service.available ? 'Available' : 'Coming Soon'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700">
                Add Integration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarIntegration;
